import { useEffect, useMemo } from "react";
import { Form, Modal, Select } from "antd";

import { normalizeApiError } from "../../../shared/errors/normalizeApiError";
import { toFormFieldErrors } from "../../../shared/errors/fieldErrors";
import { appNotification } from "../../../shared/notifications/appNotification";
import type { SellerOptionResponse } from "../../../entities/seller/model/types";
import type { SellerProductBulkAssignRequest } from "../model/types";

interface SellerProductFormProps {
  open: boolean;
  loading?: boolean;
  sellerOptions: SellerOptionResponse[];
  productOptions: { label: string; value: string }[];
  initialSellerId?: number;
  onCancel: () => void;
  onSubmit: (values: SellerProductBulkAssignRequest) => Promise<void>;
  onSearchSeller: (search: string) => void;
  onSearchProduct: (search: string) => void;
  onLoadMoreProducts: () => void;
  hasMoreProducts: boolean;
}

export const SellerProductForm = ({
  open,
  loading = false,
  sellerOptions,
  productOptions,
  initialSellerId,
  onCancel,
  onSubmit,
  onSearchSeller,
  onSearchProduct,
  onLoadMoreProducts,
  hasMoreProducts,
}: SellerProductFormProps) => {
  const [form] = Form.useForm<SellerProductBulkAssignRequest>();

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (initialSellerId) {
      form.setFieldsValue({ seller_id: initialSellerId, product_codes: [] });
    }
  }, [form, initialSellerId, open]);

  const sellerSelectOptions = useMemo(
    () =>
      sellerOptions.map((seller) => ({
        value: seller.user_id,
        label: seller.display_name
          ? `${seller.display_name} (${seller.email})`
          : seller.email,
      })),
    [sellerOptions],
  );

  const handleFinish = async (values: SellerProductBulkAssignRequest) => {
    try {
      await onSubmit(values);
      form.resetFields();
    } catch (error) {
      const apiError = normalizeApiError(error);
      const fieldErrors = toFormFieldErrors(apiError);
      if (fieldErrors.length) {
        form.setFields(fieldErrors as never);
      }
      appNotification.error({
        message: "保存に失敗しました",
        description: apiError.message || "もう一度お試しください。",
      });
    }
  };

  return (
    <Modal
      open={open}
      destroyOnHidden
      centered
      width={720}
      className="seller-product-form-modal"
      title="担当商品を追加"
      okText="紐付け登録"
      cancelText="キャンセル"
      confirmLoading={loading}
      onCancel={onCancel}
      onOk={() => form.submit()}
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          label="販売担当者"
          name="seller_id"
          rules={[{ required: true, message: "販売担当者を選択してください" }]}
        >
          <Select
            showSearch
            allowClear
            placeholder="販売担当者を選択"
            options={sellerSelectOptions}
            filterOption={false}
            disabled={initialSellerId !== undefined}
            onSearch={onSearchSeller}
            onOpenChange={(openSelect) => {
              if (openSelect && sellerOptions.length === 0) {
                onSearchSeller("");
              }
            }}
          />
        </Form.Item>

        <Form.Item
          label="商品コード"
          name="product_codes"
          rules={[{ required: true, message: "商品を1件以上選択してください" }]}
        >
          <Select
            mode="multiple"
            showSearch
            allowClear
            placeholder="商品を選択"
            options={productOptions}
            filterOption={false}
            loading={loading}
            onSearch={onSearchProduct}
            onPopupScroll={(event) => {
              const dropdown = event.currentTarget;
              const remaining =
                dropdown.scrollHeight -
                dropdown.scrollTop -
                dropdown.clientHeight;
              if (hasMoreProducts && remaining < 48) onLoadMoreProducts();
            }}
            onOpenChange={(openSelect) => {
              if (openSelect && productOptions.length === 0) {
                onSearchProduct("");
              }
            }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
